import { 
    AddEmoteDto,
    AgingDto,
    AssociateRequestDto,
    AssociateRequestsDto,
    CommentDto,
    CreateFeedbackDto,
    CreateStatusDto,
    CreateStatusesDto,
    DashboardFilterDto,
    DateRangeDto,
    EditDevicesDto,
    EditStatusDto,
    IAddEmoteDto,
    IAgingDto,
    IAssociateRequestDto,
    IAssociateRequestsDto,
    ICommentDto,
    ICreateFeedbackDto,
    ICreateStatusDto,
    ICreateStatusesDto,
    IDashboardFilterDto,
    IEditDevicesDto,
    IEditStatusDto,
    IListQueryDto,
    IReassignRequestDto,
    IRouteRequest,
    ITagDto,
    ListBuilderFilter,
    ListQueryDto,
    PaginatedListOfRequestDto,
    ReassignRequestDto,
    RequestApiClient,
    RequestDomain,
    RequestDto,
    RequestType,
    RouteRequest,
    TagDto
} from "generated/request-backend";
import { getAgingArray, getDateRange } from "app/components/helpers";
import { unifiedDashboardCardsList } from "app/models/common/enumerators";
import { IRequestPayload, IUseGenerateTokenParams } from "app/models/common/post";
import { ICommentCriteria, IDownloadVisualFlow, IRemoveCommentData, IRequestParams } from "app/models/common/request";
import { ICommentsRequest } from "app/modules/admin/visualFlow/VisualFlow";
import { API, appInsightsService, StringConstants } from "app/utils/constants";
import { useMutation, useQuery, UseQueryOptions, UseQueryResult } from "react-query";
import { AppQueryClient } from "./clients/query-client";
import { ITagsRequestAddProps, ITagsRequestProps } from "app/models/common/response";
import { replaceBase64ImagesWithBlobUrl } from "app/utils/blobStorageHelpers";

// The local environment has a trailing slash in the API_SERVER_URL, but test and prod do not.
const baseUrl : string = API.API_SERVER_URL.endsWith('/') ? API.API_SERVER_URL.slice(0,-1) : API.API_SERVER_URL;

//#region Queries
export const useQueryAllDeviceList = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        'fetchDevices',
        async () => {
            return await requestApi.lookUp_GetDeviceList();
        }
    );
};

export const useQueryAllCountryList = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        'fetchCountries',
        async () => {
            return await requestApi.lookUp_GetCountries();
        }
    );
};

export const useRequestTypes = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        'requestTypes',
        async () => {
            return await requestApi.lookUp_RequestTypes();
        }
    );
};

//fetching msx data
export const useMsxData = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        'msxData',
        async () => {
            return await requestApi.lookUp_Msx();
        }
    )
};

/**
 * Generates an Azure Blob Storage container SaS token to allow file uploads
 * @param params Parameters used to call the appropriate API route including the container name
 * @returns Azure Blob Storage container SaS token in the form of a URI
 */
export const useGenerateContainerSasToken = (params: IUseGenerateTokenParams) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const entityType = params.entity;
    return useQuery('generateContainerSasToken',
        async () => {
            if(entityType === StringConstants.REQUEST) {
                return await requestApi.file_GenerateContainerSasToken(params.containerName);
            }
            throw new Error(`Handling not found for entity type ${entityType}`);
        }
    );
};

// get article file sas url
// Currently we are only using this for Articles. However, request does have handling for this as well.
export const useGenerateFileSasToken = (entity: string, containerName: string, fileName: string = '') => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        ['generateFileSasToken', fileName], async () => {
            if(entity === StringConstants.REQUEST) {
                return await requestApi.file_GenerateFileSasToken(containerName, fileName);
            }
            throw new Error(`Handling not found for entity type ${entity}`);
        }
    );
};

//admin requests data
export const useRequestData = (data: IRequestPayload) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        ['requestQuery', data],
        async() => {
            if (Object.entries(data).length !==0) {
                let result: DashboardFilterDto = await fetchRequests(data);
                let list : PaginatedListOfRequestDto = new PaginatedListOfRequestDto();
                if(!data.isStarredRequests) {
                    list = await requestApi.request_RequestList(result);
                } else {
                    list = await requestApi.request_BookmarkRequestList(result);
                }
                for (const item of list?.items) {
                    item.description = await replaceBase64ImagesWithBlobUrl(item.description);
                    item.whyThisDateIsImportant = await replaceBase64ImagesWithBlobUrl(item.whyThisDateIsImportant);
                }
                return list;
            };
        },
        { cacheTime: 0 }
    );
};

const fetchRequests = async (payload: IRequestPayload) => {
    let data: IRequestPayload;
    const { agingKeys, isStarredRequests, dateRangeFilter, activeRange, ...payData } = payload;
    if (payload.agingKeys) 
        data = { ...payData, aging: getAgingArray(payload.agingKeys, activeRange, dateRangeFilter) };
    else
        data = { ...payData };
    data = { dateRange: getDateRange(dateRangeFilter, activeRange), ...data };

    const dateRange: DateRangeDto = new DateRangeDto({ from: new Date(data.dateRange.from), to: new Date(data.dateRange.to) });
    const iPayload: IDashboardFilterDto = { ...data, aging: [], dateRange: dateRange, requestType: data.requestType }
    if (payload.agingKeys)
    {
        const agingList: AgingDto[] = [];
        data.aging.forEach(age => {
            const iAgeRange: IAgingDto = { start: new Date(age.start), end: new Date(age.end) }
            agingList.push(new AgingDto(iAgeRange));
        });
        iPayload.aging = agingList;
    }
    const payloadData: DashboardFilterDto = new DashboardFilterDto(iPayload);
    return payloadData;
};

// list builder data
export const useRequestListBuilder = (data: ListBuilderFilter) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        ['listBuilder', data],
        async () => {
            if (data?.filter?.teamFilters?.requestTypes?.length > 0) {
                return await requestApi.request_RequestListBuilder(data);
            }
        }
    )
}

export const useListSummary = (data: ListQueryDto[]) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        ['listSummary', data],
        async () => {
            if (data?.length > 0) {
                return await requestApi.request_UserListSummary(data);
            }
        }
    );
}

export const useTeamLookUpData = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery('teamsQuery', async () => {
        return await requestApi.lookUp_Teams();
    });
};

//lookup for reasons for request reassignment

export const useReasonLookUpData = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery('reasonsQuery', async () => {
        return await requestApi.lookUp_ReasonForReassignment();
    });
};

export const useRequestTypesLookUpData = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery('requestTypesQuery', async () => {
        return await requestApi.lookUp_RequestTypes();
    });
};

//visual flow data
export const useVisualStepsData = (id: string) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        'visual',
        async () => {
            return await requestApi.visualFlow_GetVisualFlowById(id);
        },
        { cacheTime: 0 }
    );
};

export const useGetFeedback = (subUrl: string) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        'getFeedback',
        async () => {
            let isPastFeedback : boolean;
            if(subUrl.endsWith("false")) {
                isPastFeedback = false;                
            } else if (subUrl.endsWith("true")) {
                isPastFeedback = true;
            }
            return await requestApi.adminFeedback_Feedback(isPastFeedback);
        }
    );
};

/**
 * Module: End User Unified Dashboard
 * Custom hook to get list of requests for end user unified dashboard
 */
export const useRequestList = (requestFilter: IRequestParams, queryConfig?: UseQueryOptions<PaginatedListOfRequestDto, unknown>): UseQueryResult<PaginatedListOfRequestDto> => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    let requestTeam: RequestDomain[] = [];

    const filter : RequestDomain = requestFilter?.requestTeam;
    requestTeam.push(filter);

    let requestType: RequestType[] = [];
    requestFilter?.requestType?.forEach(request => {
        requestType.push(request);
    });
    
    const filterDto : DashboardFilterDto = new DashboardFilterDto({
        ...requestFilter,
        requestTeam: requestTeam[0]? requestTeam : undefined
    });

    return useQuery(
        ['useRequestList', requestFilter],
        async () => {
            if (Object.keys(requestFilter).length !== 0) {
                // Depending on the query, this could either go to request/list or request/bookmark/list.
                if(requestFilter.type === unifiedDashboardCardsList.STARRED_REQUEST)
                {
                    return await requestApi.request_BookmarkRequestList(filterDto);
                }
                else if (
                    requestFilter.type === unifiedDashboardCardsList.MY_REQUEST ||
                    requestFilter.type === unifiedDashboardCardsList.ALL_REQUEST ||
                    requestFilter.type === unifiedDashboardCardsList.SHARED_WITH_ME
                )
                {
                    return await requestApi.request_RequestList(filterDto);
                }
                else {
                    throw new Error(`Invalid request type: ${requestFilter.type}`);
                }
            }
        },
        { ...queryConfig, cacheTime: 0 }
    );
};

/**
 * Module: End User Unified Dashboard
 * Custom hook to get list of requests for end user unified dashboard
 */
export const useGetRequestById = (requestId: string, queryConfig?: UseQueryOptions<RequestDto, unknown>) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        ['getRequestById', requestId],
        async () => {
            if (requestId) {
                return await requestApi.request_GetRequestDetails(requestId);
            }
        },
        queryConfig
    );
};

/**
 * Module: Admin Team Contact Details
 * Custom hook to get list of team members and info about each of them
 */
export const useTeamContactList = (teamId: number, count: number) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(['useTeamContactList', teamId, count], async () => {
        return await requestApi.user_GetSearchGroupMembers(teamId, count);
    });
};

export const useGetNewRequest = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(
        ['useGetNewRequest'],
        async () => {
            return await requestApi.request_GetCachedRequest();
        },
        { cacheTime: 0 }
    );
};

export const useGetCountries = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(['useGetCountries'], async () => {
        return await requestApi.lookUp_GetCountries();
    });
};

export const useFetchComments = (criteria: ICommentCriteria) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(['useFetchComments', criteria], async () => {
        return await requestApi.comment_GetCommentList(null, criteria.correlationId, criteria.entityType, null, null, null, criteria.isInternal, undefined, undefined, null, criteria.sortDirection, criteria.pageNumber, criteria.pageSize);
    });
};

export const useSummaryCardsCountForUser = (subUrl: string) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(['useSummaryCardsCountForUser', subUrl], async () => {
        if (subUrl !== '' && subUrl) {
            const splitUrl : string[] = subUrl.split("&");
            let dateFrom : Date = null;
            let dateTo : Date = null;
            let hideParents : boolean = undefined;
            splitUrl.forEach(string => {
                if(string.startsWith("DateRange.From=")) {
                    dateFrom = new Date(string.split("=")[1]);
                } else if (string.startsWith("DateRange.To=")) {
                    dateTo = new Date(string.split("=")[1]);
                } else if (string.startsWith("HideParentRequests=")) {
                    hideParents = string.split("=")[1] === "true";
                }
            });
            return await requestApi.request_UserSummaryCard(dateFrom, dateTo, hideParents);
        }
    });
};

// get request details lookup
export const useRequestDetailsLookUpData = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery('useRequestDetailsLookUpData', async () => {
        return await requestApi.lookUp_RequestTypeDetails();
    });
};

// get status
export const useFetchStatus = (criteria: ICommentCriteria) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(['useFetchStatus', criteria], async () => {
        if (Object.keys(criteria).length !== 0) {
            return await requestApi.status_GetStatusList(null, criteria.correlationId, null, criteria.entityType, null, null, criteria.isInternal, null, undefined, undefined, null, criteria.sortDirection, criteria.pageNumber, criteria.pageSize);
        }
    });
};

//get tags
export const useGetTagsList = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(['getTagsList'], async () => {
        return await requestApi.tag_RequestList();
    });
};

export const useGetUsersList = (
    searchValue: string,
    team?: RequestDomain,
    count?: number
) => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    return useQuery(['getUsersList', searchValue, team], async () => {
        return await requestApi.user_GetSearchGroupMembers(team, count);
    });
};
//#endregion
//#region Mutations

export const useVisualComments = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const requestTypeMutation = useMutation((data: ICommentsRequest) => requestApi.comment_GetCommentList(null, data.correlationId,data.entintyType,null,null,null,null,undefined,undefined,null,null,data.pageNumber,data.pageSize), {
        onSuccess: () => {
        }
    });
    return requestTypeMutation;
};

// Currently getting a 500 error, but that's what we're seeing on main as well. However, this might be expected on local branches. This calls the RouteController.cs route that uses the ML model.
export const useGettingRequestType = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const requestTypeMutation = useMutation((data: IRouteRequest) => requestApi.route_IdentifyDomain(new RouteRequest(data)), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
            }, 2000);
            appInsightsService.logEvent('useGettingRequestType was Succesful');
        }
    });
    return requestTypeMutation;
};

//feedback submission
export const usePostFeedback = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const requestTypeMutation = useMutation((data: ICreateFeedbackDto) => requestApi.adminFeedback_CreateFeedback(new CreateFeedbackDto(data)), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
            }, 2000);
            appInsightsService.logEvent('usePostFeedback was Succesful');
        }
    });
    return requestTypeMutation;
};

export const usePostFeedbackResponse = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const requestTypeMutation = useMutation(
        ({id, response}: {id: string, response: string}) => requestApi.adminFeedback_Respond(id, response),
        {
            onSuccess: () => {
                setTimeout(() => {
                    AppQueryClient.invalidateQueries('useNotificationsCount');
                }, 2000);
                appInsightsService.logEvent('usePostFeedbackResponse was Succesful');
            }
        }
    );
    return requestTypeMutation;
};

//request Reassign
export const useRequestReassign = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const requestTypeMutation = useMutation((data: IReassignRequestDto) => requestApi.reassign_Reassign(new ReassignRequestDto(data)), {
        onSuccess: () => {
            AppQueryClient.invalidateQueries('useNotificationsCount');
            AppQueryClient.invalidateQueries('getRequestById');
            AppQueryClient.invalidateQueries('requestQuery');
            AppQueryClient.invalidateQueries('useRequestList');
            AppQueryClient.invalidateQueries('listBuilder');
        },
        onError: () => {
            AppQueryClient.invalidateQueries('useNotificationsCount');
            AppQueryClient.invalidateQueries('getRequestById');
            AppQueryClient.invalidateQueries('requestQuery');
            AppQueryClient.invalidateQueries('useRequestList');
            AppQueryClient.invalidateQueries('listBuilder');
        }
    });
    return requestTypeMutation;
};

export const useSaveRequest = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const requestTypeMutation = useMutation((data: string) => requestApi.request_SetCachedRequest(data), {
        onSuccess: () => {
            appInsightsService.logEvent('useSaveRequest was Succesful');
        }
    });
    return requestTypeMutation;
};

export const useAddBookmark = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const requestTypeMutation = useMutation((requestId: string) => requestApi.request_BookmarkRequest(requestId), {
        onSuccess: () => {
            AppQueryClient.invalidateQueries('useSummaryCardsCountForUser');
            appInsightsService.logEvent('useAddBookmark was Succesful');
        }
    });
    return requestTypeMutation;
};

export const useRemoveBookmark = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const requestTypeMutation = useMutation((requestId: string) => requestApi.request_BookmarkRequestDelete(requestId), {
        onSuccess: () => {
            AppQueryClient.invalidateQueries('useSummaryCardsCountForUser');
            appInsightsService.logEvent('useRemoveBookmark was Succesful');
        }
    });
    return requestTypeMutation;
};

/**
 * Mutation used to submit comment
 */
export const useSubmitComment = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: ICommentDto) => requestApi.comment_CreateComment(new CommentDto({...data})), {
        onMutate: async () => {
            AppQueryClient.invalidateQueries('useFetchComments');
        },
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('useFetchComments');
            AppQueryClient.invalidateQueries('useNotificationsCount');
            AppQueryClient.invalidateQueries('getRequestById');
            AppQueryClient.invalidateQueries('requestQuery');
            AppQueryClient.invalidateQueries('useRequestList');
            AppQueryClient.invalidateQueries('listBuilder');
            appInsightsService.logEvent('useSubmitComment was Succesful');
        },
        onError: () => {
            AppQueryClient.invalidateQueries('useFetchComments');
            AppQueryClient.invalidateQueries('getRequestById');
            AppQueryClient.invalidateQueries('requestQuery');
            AppQueryClient.invalidateQueries('useRequestList');
            AppQueryClient.invalidateQueries('listBuilder');
            appInsightsService.logEvent('useSubmitComment Failed');
        }
    });
    return mutation;
};

/**
 * Mutation used to edit comment
 */
export const useEditComment = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: ICommentDto) => requestApi.comment_UpdateComment(new CommentDto(data)), {
        onMutate: async () => {
            AppQueryClient.invalidateQueries('useFetchComments');
        },
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('useFetchComments');
            AppQueryClient.invalidateQueries('useNotificationsCount');
            appInsightsService.logEvent('useEditComment was Succesful');
        },
        onError: () => {
            AppQueryClient.invalidateQueries('useFetchComments');
            appInsightsService.logEvent('useEditComment Failed');
        }
    });
    return mutation;
};

/**
 * Mutation to download visual flow
 */
export const useDownloadVisualFlow = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: IDownloadVisualFlow) => requestApi.visualFlow_GetDownloadVisualFlowById(data.correlationId), {
        onSuccess: () => {
            appInsightsService.logEvent('useDownloadVisualFlow was Succesful');
        }
    });
    return mutation;
};

/**
 * Mutation used to remove comment
 */
export const useRemoveComment = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: IRemoveCommentData) => requestApi.comment_RemoveComment(data.id, data.correlationId, data.entityType), {
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('useFetchComments');
            AppQueryClient.invalidateQueries('useNotificationsCount');
            appInsightsService.logEvent('useRemoveComment was Succesful');
        }
    });
    return mutation;
};

/**
 * Mutation used to hide a request
 * */
export const useHideRequest = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const response = useMutation((id: string) => requestApi.request_HideRequest(id), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useHideRequest was Succesful');
        }
    });
    return response;
};

/**
 * Mutation used to unhide a request
 * */
export const useUnhideRequest = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const response = useMutation((id: string) => requestApi.request_UnHideRequest(id), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useUnhideRequest was Succesful');
        }
    });
    return response;
};

/**
 * Mutation used to submit status
 */
export const useSubmitStatus = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: ICreateStatusDto) => requestApi.status_CreateStatus(new CreateStatusDto(data)), {
        onMutate: async () => {
            AppQueryClient.invalidateQueries('useFetchStatus');
        },
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('useFetchStatus');
            AppQueryClient.invalidateQueries('useNotificationsCount');
            appInsightsService.logEvent('useSubmitStatus was Succesful');
        },
        onError: () => {
            AppQueryClient.invalidateQueries('useFetchStatus');
            appInsightsService.logEvent('useSubmitStatus Failed');
        }
    });
    return mutation;
};

/**
 * Mutation used to submit statuses for multiple requests
 * Primarily used for parent/child relationships
 */
export const useSubmitStatuses = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((statuses: ICreateStatusesDto) => requestApi.status_CreateStatuses(new CreateStatusesDto(statuses)), {
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('useFetchStatus');
            AppQueryClient.invalidateQueries('useNotificationsCount');
            appInsightsService.logEvent('useSubmitStatuses was Succesful');
        },
        onError: () => {
            AppQueryClient.invalidateQueries('useFetchStatus');
            appInsightsService.logEvent('useSubmitStatuses Failed');
        }
    });
    return mutation;
};

/**
 * Mutation used to edit status
 */
export const useEditStatusResponse = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: ICreateStatusDto) => requestApi.status_UpdateStatus(new CreateStatusDto(data)), {
        onMutate: async () => {
            AppQueryClient.invalidateQueries('useFetchStatus');
        },
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('useFetchStatus');
            AppQueryClient.invalidateQueries('useNotificationsCount');
            appInsightsService.logEvent('useEditStatusResponse was Succesful');
        },
        onError: () => {
            AppQueryClient.invalidateQueries('useFetchStatus');
            appInsightsService.logEvent('useEditStatusResponse Failed');
        }
    });
    return mutation;
};

/**
 * Mutation used to remove status
 */
export const useRemoveStatus = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: IRemoveCommentData) => requestApi.status_RemoveStatus(data.id, data.correlationId), {
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('useFetchStatus');
            AppQueryClient.invalidateQueries('useNotificationsCount');
            appInsightsService.logEvent('useRemoveStatus was Succesful');
        }
    });
    return mutation;
};

// add tag
export const useAddTag = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((tag: ITagDto) => requestApi.tag_CreateTag(new TagDto(tag)), {
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('getTagsList');
            appInsightsService.logEvent('useAddTag was Succesful');
        }
    });
    return mutation;
};

// delete tag
export const useDeleteTag = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((tag: string) => requestApi.tag_DeleteTag(tag), {
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('getTagsList');
            appInsightsService.logEvent('useDeleteTag was Succesful');
        }
    });
    return mutation;
};

// add tag from manage request
export const useAddTagRequest = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: ITagsRequestAddProps) => requestApi.request_UpdateTagInRequest(data.requestId, data.selectedValues), {
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('requestQuery');
            AppQueryClient.invalidateQueries('listBuilder');
            appInsightsService.logEvent('useAddTagRequest was Succesful');
        }
    });
    return mutation;
};

// delete tag from manage request
// Currently delete is not being used. Instead it looks like "useAddTagRequest" is updating the list of tags.
export const useDeleteTagRequest = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: ITagsRequestProps) => requestApi.request_RemoveTagFromRequest(data.requestId, new TagDto({ ...data })), {
        onSuccess: () => {
            // Query Invalidations
            AppQueryClient.invalidateQueries('requestQuery');
            AppQueryClient.invalidateQueries('listBuilder');
            appInsightsService.logEvent('useDeleteTagRequest was Succesful');
        }
    });
    return mutation;
};

//Add comment emotes
export const useAddCommentEmote = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const response = useMutation((data: IAddEmoteDto) => requestApi.comment_AddEmote(new AddEmoteDto(data)), {
        onSuccess: () => {
            AppQueryClient.invalidateQueries('useFetchComments');
            appInsightsService.logEvent('useAddCommentEmote was Succesful');
        }
    });

    return response;
};

/**
 * Mutation used to update the Devices of a request and its Parent if it is a child
 */
export const useUpdateRequestDevices = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: IEditDevicesDto) => requestApi.request_UpdateDevices(new EditDevicesDto(data)), {
        onMutate: async () => {
            await AppQueryClient.cancelQueries('requestQuery');
        },
        onSuccess: () => {
            AppQueryClient.invalidateQueries('requestQuery');
            AppQueryClient.invalidateQueries(StringConstants.LIST_BUILDER_QUERY);
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
            }, 2000);
            appInsightsService.logEvent('useUpdateRequestDevices was Succesful');
        },
        onError: () => {
            AppQueryClient.invalidateQueries('requestQuery');
            AppQueryClient.invalidateQueries(StringConstants.LIST_BUILDER_QUERY);
            appInsightsService.logEvent('useUpdateRequestDevices Failed');
        }
    });
    return mutation;
};

/**
 * Mutation used to update the status of a request
 */
export const useUpdateStatus = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: IEditStatusDto) => requestApi.request_UpdateStatusRequest(new EditStatusDto(data)), {
        onMutate: async () => {
            await AppQueryClient.cancelQueries('requestQuery');
        },
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
            }, 2000);
            appInsightsService.logEvent('useUpdateStatus was Succesful');
        },
        onError: () => {
            AppQueryClient.invalidateQueries('requestQuery');
            appInsightsService.logEvent('useUpdateStatus Failed');
        }
    });
    return mutation;
};

export const useAssociateRequests = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: IAssociateRequestsDto) => requestApi.request_AssociateRequests(new AssociateRequestsDto(data)), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationCount');
                AppQueryClient.invalidateQueries('getRequestById');
                AppQueryClient.invalidateQueries('useRequestList');
                AppQueryClient.invalidateQueries('requestQuery');
            }, 2000);
            appInsightsService.logEvent('useAssociateRequests was Succesful');
        }
    });

    return mutation;
};

export const useUnassociateRequest = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: IAssociateRequestDto) => requestApi.request_UnassociateRequest(new AssociateRequestDto(data)), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationCount');
            }, 2000);
            AppQueryClient.invalidateQueries('getRequestById');
            AppQueryClient.invalidateQueries('useRequestList');
            AppQueryClient.invalidateQueries('requestQuery');
            appInsightsService.logEvent('useUnassociateRequest was Succesful');
        }
    });

    return mutation;
};

/**
 * Mutation to download list builder results
 */
export const useDownloadListBuilderItems = () => {
    const requestApi : RequestApiClient = new RequestApiClient(baseUrl);
    const mutation = useMutation((data: IListQueryDto) => requestApi.request_GetDownloadVisualFlowById(new ListQueryDto(data)), {
        onSuccess: () => {
            appInsightsService.logEvent('The results of the list builder query have been successfully downloaded');
        }
    });
    return mutation;
};
//#endregion